import { useState } from 'react';
import { books } from '../../mocks/books';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import Seo from '@/components/feature/Seo';
import { pageSeo } from '@/mocks/seo';

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', phone: '', message: '' });
    }, 3000);
  };

  const contactAnim = useScrollAnimation({ threshold: 0.1 });
  const bestsellerAnim = useScrollAnimation();
  const browseAnim = useScrollAnimation({ threshold: 0.2 });

  return (
    <main>
      <Seo {...pageSeo.contactUs} />
      {/* Contact Form Section */}
      <section className="w-full py-12 md:py-16 bg-white overflow-hidden">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={contactAnim.ref} className={`flex flex-col lg:flex-row items-start gap-8 lg:gap-12 animate-on-scroll ${contactAnim.isVisible ? 'animate-visible' : ''}`}>
            {/* Form */}
            <div className="flex-1 w-full">
              <p className="text-xs text-navy-500 uppercase tracking-wider mb-2">Any Questions?</p>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900 mb-2">
                Let our <span className="text-navy-500">book experts</span> help you
              </h1>
              <p className="text-sm text-navy-700 mb-8">Feel free to Leave us a message</p>

              {submitted ? (
                <div className="bg-navy-50 border border-navy-200 rounded-lg p-6 text-center animate-scale-in">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-navy-500 mx-auto mb-4">
                    <i className="ri-check-line text-white text-xl"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-navy-900 mb-2">Message Sent!</h3>
                  <p className="text-sm text-navy-700">Thank you for reaching out. We'll get back to you soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="group">
                      <label className="block text-sm font-medium text-navy-900 mb-1">Name</label>
                      <input
                        type="text"
                        placeholder="Enter your name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 text-sm border border-cream-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500 transition-all duration-300"
                        required
                      />
                    </div>
                    <div className="group">
                      <label className="block text-sm font-medium text-navy-900 mb-1">Email Address</label>
                      <input
                        type="email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 text-sm border border-cream-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500 transition-all duration-300"
                        required
                      />
                    </div>
                  </div>
                  <div className="group">
                    <label className="block text-sm font-medium text-navy-900 mb-1">Contact Number</label>
                    <input
                      type="tel"
                      placeholder="Enter your contact number"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 text-sm border border-cream-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500 transition-all duration-300"
                    />
                  </div>
                  <div className="group">
                    <label className="block text-sm font-medium text-navy-900 mb-1">About the book/Other queries</label>
                    <textarea
                      placeholder="Leave a message"
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-3 text-sm border border-cream-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500 resize-none transition-all duration-300"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-8 py-3 text-sm font-medium text-white bg-navy-500 rounded-full hover:bg-navy-600 transition-all duration-300 hover:scale-105"
                  >
                    Send
                  </button>
                </form>
              )}
            </div>

            {/* Image */}
            <div className="flex-1 hidden lg:flex items-center justify-center">
              <div className="w-full max-w-[450px] animate-float">
                <img
                  src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/https://leavittpeakpress.com/wp-content/uploads/2025/02/Banner-4.png"
                  alt="Contact Us"
                  className="w-full h-auto rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Discover Best Sellers */}
      <section className="w-full py-12 md:py-16 bg-cream-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={bestsellerAnim.ref} className={`flex flex-col lg:flex-row items-center gap-8 lg:gap-12 animate-on-scroll ${bestsellerAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="flex-1">
              <p className="text-xs text-navy-500 uppercase tracking-wider mb-2">Featured Book</p>
              <h2 className="text-2xl md:text-3xl font-bold text-navy-900 mb-4">Discover Our Best Sellers</h2>
              <p className="text-sm text-navy-700 leading-relaxed mb-6">
                Meet the visionary authors of Leavitt Peak Press, whose literary prowess has propelled their works to remarkable success in the market. Through their dedication, creativity, and profound storytelling, they've captured the hearts and minds of readers worldwide, achieving unparalleled sales and acclaim.
              </p>
              <a
                href="https://www.amazon.com/s?k=leavitt+peak+press"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex px-6 py-3 text-sm font-medium text-white bg-navy-500 rounded-full hover:bg-navy-600 transition-all duration-300 hover:scale-105"
              >
                Get the book
              </a>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="flex items-end gap-4">
                <div className="w-32 md:w-40 rotate-[-12deg] animate-float">
                  <img
                    src={books[1].cover}
                    alt={books[1].title}
                    className="w-full h-auto rounded-md transition-transform duration-500 hover:scale-105 hover:rotate-[0deg]"
                  />
                </div>
                <div className="w-36 md:w-44 z-10 animate-float-delayed">
                  <img
                    src={books[2].cover}
                    alt={books[2].title}
                    className="w-full h-auto rounded-md transition-transform duration-500 hover:scale-105"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Browse Collection CTA */}
      <section className="w-full py-12 md:py-16 bg-navy-500 relative overflow-hidden">
        <div className="absolute inset-0 animate-shimmer pointer-events-none"></div>
        <div ref={browseAnim.ref} className={`w-full px-4 md:px-6 lg:px-8 relative z-10 animate-on-scroll ${browseAnim.isVisible ? 'animate-visible' : ''}`}>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex-1">
              <p className="text-xs text-navy-200 uppercase tracking-wider mb-2">Looking for new reads?</p>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Browse our collection</h2>
              <p className="text-sm text-navy-100 leading-relaxed max-w-2xl">
                Browse through our extensive catalog showcasing the diverse array of published books brought to you by Leavitt Peak Press.
              </p>
            </div>
            <a
              href="https://www.amazon.com/s?k=leavitt+peak+press"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 text-sm font-medium text-navy-500 bg-white rounded-full hover:bg-cream-100 transition-all duration-300 hover:scale-105 whitespace-nowrap shrink-0"
            >
              Browse All
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}